/**
 * Preview modal — renders the candidate-facing slot-picker UI
 * inside an iframe so the scheduler can confirm the email's landing
 * page looks right before sending.
 *
 * Mirrors the legacy `SlotsPreview` + `SchFindTimeField`:
 *
 *   1. Open a kula modal with an iframe pointing at
 *      `schedule.kula.ai/preview` (prod) or the stg equivalent.
 *   2. On iframe load, `postMessage` the interview + slot data into
 *      the candidate app via `{ event: 'candidatePreview', data }`.
 *   3. The candidate app renders a real preview of the time-picker
 *      the recipient will see.
 */
import { useRef, useState } from 'react';
import { Box } from '@chakra-ui/react';
import { KModal } from '@/ui-library/components';
import { KCenterSpinner } from '@/ui-library/components/molecules/kLoaders';

// ─────────────────────────────────────────────────────────────────
// Tuning constants — iframe origin per environment + fade-in
// timing. Tweak here, not in the component body.
// ─────────────────────────────────────────────────────────────────

const PROD_PREVIEW_URL = 'https://schedule.kula.ai/preview';
const STAGING_PREVIEW_URL = 'https://schedule.kulastg.com/preview';

/** ms to wait after posting data before revealing the iframe —
 *  lets the candidate app settle first so the user doesn't see a
 *  flash of the unstyled loading state. */
const REVEAL_DELAY_MS = 100;

// ─────────────────────────────────────────────────────────────────
// Shape the candidate app expects on the postMessage. Matches the
// legacy `iFrameData` bag exactly so we don't have to change the
// receiving end.
// ─────────────────────────────────────────────────────────────────

export interface SlotsPreviewData {
  /** Identifies Kula-side sender. Legacy passes 'kula'. */
  src: string;
  /** Free-form style overrides (legacy forwards an empty object). */
  styleProps?: Record<string, unknown>;
  /** Interview name — shows as the title on the preview page. */
  name?: string;
  /** Interview duration in minutes. */
  duration?: number;
  /** IANA tz like "Asia/Kolkata" — drives the displayed times. */
  timeZone?: string;
  /** 'google_meet' / 'zoom' / 'onsite' / etc. — drives the
   *  location row on the preview page. */
  location_type?: string;
  contact_name?: string;
  contact_email?: string;
  /** The emitted slot list — `{ start, end }[]` ISO 8601. */
  slots: Array<{ start: string; end: string }>;
  /** User's date format (`'MM/DD/YYYY'`, etc.) — sent through to
   *  the preview app for consistent display with the main UI. */
  format?: string;
}

export interface SlotsPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  data: SlotsPreviewData;
}

export function SlotsPreviewModal({
  isOpen,
  onClose,
  data,
}: SlotsPreviewModalProps) {
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  // Early return below (`if (!isOpen) return null`) guarantees this
  // component only mounts while the modal is open, so `useState(true)`
  // is a fresh "loading" start on every open — no reset effect needed.
  const [isLoading, setIsLoading] = useState(true);

  const iframeUrl =
    import.meta.env.VITE_ENV === 'production'
      ? PROD_PREVIEW_URL
      : STAGING_PREVIEW_URL;

  const handleIframeLoad = () => {
    iframeRef.current?.contentWindow?.postMessage(
      { event: 'candidatePreview', data },
      '*'
    );
    setTimeout(() => setIsLoading(false), REVEAL_DELAY_MS);
  };

  if (!isOpen) return null;

  return (
    <KModal
      isOpen={isOpen}
      title="Preview of candidate's slot selection"
      onClose={onClose}
      ctaAction={() => {}}
      size="4xl"
      modalBodyProps={{
        maxHeight: '640px',
        overflow: 'hidden',
      }}
      footer={false}
    >
      <Box>
        {isLoading && <KCenterSpinner />}
        <Box opacity={isLoading ? 0 : 1}>
          <iframe
            ref={iframeRef}
            title="candidate-ui-preview"
            width="840px"
            height="580px"
            style={{ overflow: 'hidden', border: 'none' }}
            src={iframeUrl}
            onLoad={handleIframeLoad}
          />
        </Box>
      </Box>
    </KModal>
  );
}
